/**
 * The gallery's contacts. No search, no directory — every row is somebody an act named.
 */
import type {
  AddContactRequest,
  Contact,
  ContactEvent,
  ContactTimelineResponse,
  ContactsResponse,
  ExportRecord,
  ExportRecordsResponse,
  ReaderStamp,
  SetContactNoteRequest,
} from "@ondo/shared";

import type { ApiFile } from "./request.ts";
import { request, requestFile, requestWith } from "./request.ts";

/**
 * `?actingAs=` — which of the reader's own firms this read is made as.
 *
 * NOT A SEARCH AND NOT AN IDENTITY CLAIM. The only value the screen ever puts
 * here is `workspace.organisation.ondoId`, which came from `/v1/me`, and
 * `ondo.collector_list_reader_stamp()` refuses any id the caller does not hold
 * a seat in — so what the browser supplies is at most a CHOICE between the
 * reader's own seats. The reader's name and ONDO id are never sent: they are
 * read from `auth.uid()` on the server.
 */
const actingParam = (actingAs: string | null): string =>
  actingAs ? `?actingAs=${encodeURIComponent(actingAs)}` : "";

/**
 * GET /v1/gallery/contacts — everyone an act of this gallery has named, and
 * the stamp that says who is looking.
 *
 * NO SEARCH, AND THERE IS NO OVERLOAD THAT TAKES ONE. Not a directory, and no
 * `?q=`: the filtering on the screen is over rows already in the browser. An
 * ONDO id is attached, never discovered, so a lookup parameter here would be
 * the one endpoint `CLAUDE.md` says may never exist.
 *
 * THE STAMP COMES BACK WITH THE ROWS, on purpose. Fetching it separately would
 * leave a window in which a confidential list is on screen with nobody's name
 * on it — see `ContactsResponse`.
 */
export const fetchContacts = (
  actingAs: string | null,
): Promise<{ contacts: Contact[]; stamp: ReaderStamp }> =>
  request<ContactsResponse>(`/gallery/contacts${actingParam(actingAs)}`);

/**
 * GET /v1/gallery/contacts/export — the collector list as a file the SERVER
 * built.
 *
 * This replaced a `Blob` assembled in the browser out of rows already on the
 * page. The difference that matters is not where the string is concatenated:
 * it is that `ondo.export_collector_list()` writes an audit row naming the
 * reader, the firm, the scope and every record BEFORE these bytes exist, and
 * refuses the export if it cannot.
 *
 * The filter and the search are sent because the file is what is on screen —
 * anything wider would make the button a different disclosure from the page it
 * sits on — and they are applied in SQL and recorded on the audit row.
 */
export const exportCollectorList = (scope: {
  actingAs: string | null;
  filter: string | null;
  search: string | null;
}): Promise<ApiFile> => {
  const query = new URLSearchParams();
  if (scope.actingAs) query.set("actingAs", scope.actingAs);
  if (scope.filter) query.set("filter", scope.filter);
  if (scope.search) query.set("search", scope.search);

  const suffix = query.size === 0 ? "" : `?${query.toString()}`;
  return requestFile(`/gallery/contacts/export${suffix}`);
};

/**
 * GET /v1/gallery/contacts/exports — who has taken this list out of ONDO.
 *
 * Takes nothing. A reader sees their own exports and an organisation's admins
 * see every export taken in its name, which is `collector_list_exports_read`
 * and not a parameter.
 */
export const fetchExportRecords = (): Promise<ExportRecord[]> =>
  request<ExportRecordsResponse>("/gallery/contacts/exports").then((body) => body.exports);

/**
 * GET /v1/gallery/contacts/works/:workId/image — a work's photograph with this
 * reader's name burnt into it by the server.
 *
 * NOT A SIGNED STORAGE URL, which is what every other image in this app is.
 * Inside the collector list the bytes have to carry who was shown them, so the
 * API fetches and composites them; a signed URL in the browser IS the
 * unstamped original. The answer is bytes, so it becomes an object URL — and
 * the caller owns revoking it.
 */
export const fetchStampedWorkImage = (
  workId: string,
  actingAs: string | null,
  size: "thumb" | "full" = "thumb",
): Promise<Blob> => {
  const query = new URLSearchParams({ size });
  if (actingAs) query.set("actingAs", actingAs);

  return requestFile(
    `/gallery/contacts/works/${encodeURIComponent(workId)}/image?${query.toString()}`,
  ).then((file) => file.blob);
};

/**
 * POST /v1/gallery/contacts — add one contact by hand.
 *
 * An ONDO id alone, or a name (optionally with an email, which links the
 * contact to an ONDO identity — minted or matched, indistinguishably, and
 * rate-limited). NOT a search: nothing is looked up and nothing comes back.
 * The screen refetches the list, where the new row arrives merged.
 */
export const addContact = (body: AddContactRequest): Promise<void> =>
  request<void>("/gallery/contacts", body);

/**
 * DELETE /v1/gallery/contacts/added/:id — retract an added contact.
 *
 * The row is ended rather than removed, and derived relationships with the
 * same person are untouched: retracting an add never unsays a sale.
 */
export const endAddedContact = (addedId: string): Promise<void> =>
  requestWith<void>("DELETE", `/gallery/contacts/added/${encodeURIComponent(addedId)}`);

/**
 * PUT /v1/gallery/contacts/note — the firm's own note on a contact.
 *
 * Takes an ONDO id OR a name, never both: half a gallery's contacts are text it
 * typed on its own sales and have no id to be addressed by.
 */
export const setContactNote = (body: SetContactNoteRequest): Promise<void> =>
  requestWith<void>("PUT", "/gallery/contacts/note", body);

/** DELETE /v1/gallery/contacts/note/:id — stop keeping one. The row survives. */
export const endContactNote = (noteId: string): Promise<void> =>
  requestWith<void>("DELETE", `/gallery/contacts/note/${encodeURIComponent(noteId)}`);

/**
 * GET /v1/gallery/contacts/:ondoId/timeline — what you and one contact did.
 *
 * The id comes from `fetchContacts`, never from a form. An id that is not
 * already a contact returns an EMPTY LIST rather than a 404, because a 404 would
 * distinguish "no such id" from "not yours" and that distinction is the oracle
 * the whole id space is kept closed to avoid.
 */
export const fetchContactTimeline = (ondoId: string): Promise<ContactEvent[]> =>
  request<ContactTimelineResponse>(
    `/gallery/contacts/${encodeURIComponent(ondoId)}/timeline`,
  ).then((body) => body.events);
